import Card from "@/components/Card"
import InputError from "@/components/InputError"
import SkeletonRow from "@/components/SkeletonRow"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import api from "@/lib/axios"
import { getInitials } from "@/lib/utils"
import { DataSiswa } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaCheck, FaFaceSmileBeam } from "react-icons/fa6"
import { GiBackwardTime } from "react-icons/gi"
import { MdBlockFlipped } from "react-icons/md"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"

type DataPembayaran = {
  id_data_pendaftaran: string
  status_text?: string
  jalur_jenis: string
  ukt: number
  status_pembayaran: string
  is_cek_butawarna: boolean | number
  siswa: DataSiswa
  nomor_pendaftaran: string
  pilihan_prodi: string
  status_loket_1?: boolean | number | null
  keterangan_loket_1?: string | null
}

type ValidasiState = {
  id_data_pendaftaran?: string
  keterangan_loket_1: string
  status_loket_1?: number
}

const getDataPembayaran = async (searchQuerySiswa: string) => {
  const response = await api.get(
    `/verifikator/daftar-kembali/loket-1/data-pembayaran?id=${searchQuerySiswa}`
  )
  return response.data
}

const patchDataValidasi = async (form: ValidasiState) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/loket-1/validasi",
    form
  )
  return response.data
}

const patchDataResetValidasi = async (id_data_pendaftaran: string) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/reset?loket=1",
    { id_data_pendaftaran }
  )
  return response.data
}

export default function Loket1() {
  const [searchParams] = useSearchParams()
  const idParams = searchParams.get("id")

  const {
    data: dataPembayaranSiswa,
    isPending,
    error,
  } = useQuery<{ data: DataPembayaran }, AxiosError>({
    queryKey: ["dataPembayaranSiswa", idParams],
    queryFn: () => getDataPembayaran(idParams || ""),
    enabled: !!idParams,
  })
  const data: DataPembayaran | undefined = dataPembayaranSiswa?.data

  const [form, setForm] = useState<ValidasiState>({
    keterangan_loket_1: "",
  })

  const queryClient = useQueryClient()
  const {
    mutate,
    isPending: isPendingValidation,
    error: errorValidation,
  } = useMutation({
    mutationFn: patchDataValidasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataPembayaranSiswa"],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const {
    mutate: mutateResetValidation,
    isPending: isPendingResetValidation,
    // error: errorResetValidation,
  } = useMutation({
    mutationFn: patchDataResetValidasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataPembayaranSiswa"],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const handleValidasi = (status: number) => {
    mutate({
      ...form,
      id_data_pendaftaran: data?.id_data_pendaftaran,
      status_loket_1: status,
    })
  }

  const handleReset = () => {
    mutateResetValidation(data?.id_data_pendaftaran || "")
  }

  if (isPending) {
    return (
      <div className="flex flex-col gap-y-3">
        {Array(10)
          .fill(null)
          .map((_, index) => {
            return <SkeletonRow key={index} />
          })}
      </div>
    )
  }

  if (error?.response?.status === 404) {
    return (
      <div>
        Data <span className="text-primary">{idParams}</span> tidak ditemukan.
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div>
          <FaFaceSmileBeam className="mx-auto" size={40} />
          <h1 className="mt-3 text-xl font-semibold">
            Lakukan pencarian menggunakan nomor pendaftaran atau nama siswa.
          </h1>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex lg:flex-row flex-col gap-3">
      <Avatar className="w-16 h-16">
        <AvatarImage src={data?.siswa?.path_foto || ""} alt={"foto"} />
        <AvatarFallback>
          {getInitials(data?.siswa?.nama_lengkap || "")}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 overflow-auto">
        <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
          <tbody>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Nomor Pendaftaran
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.nomor_pendaftaran}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Nama Peserta
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.siswa.nama_lengkap}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Nomor Handphone
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.siswa?.handphone}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Program Studi
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">Ilmu Komputer (S1)</td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Cek Buta Warna
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.is_cek_butawarna ? "Ya" : "Tidak"}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Jalur
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">{data?.jalur_jenis}</td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Status Pembayaran
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.status_pembayaran === "FREE" ? (
                  <Badge variant={"dark"}>Penerima KIPK</Badge>
                ) : data?.status_pembayaran === "SUDAH_BAYAR" ? (
                  <Badge variant={"dark"}>Sudah Bayar</Badge>
                ) : (
                  data?.status_pembayaran === "BELUM_BAYAR" && (
                    <Badge variant={"secondary"}>Belum Bayar</Badge>
                  )
                )}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Status Validasi Loket
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.status_loket_1 === 1 ? (
                  <Badge variant={"dark"}>Sudah Valid</Badge>
                ) : data?.status_loket_1 === 0 ? (
                  <Badge variant={"outline"}>Tidak Valid</Badge>
                ) : (
                  <Badge variant={"secondary"}>Belum Valid</Badge>
                )}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Catatan
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.keterangan_loket_1}
              </td>
            </tr>
          </tbody>
        </table>
        {data?.status_loket_1 === null ? (
          <Card className="mt-3 px-4">
            <div>
              <Label>Input Catatan</Label>
              <Textarea
                placeholder="Ketik catatan..."
                className="mt-1"
                value={form.keterangan_loket_1}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    keterangan_loket_1: event.target.value,
                  }))
                }
              />
              <InputError
                text={errorValidation?.response?.data.errors?.keterangan_loket_1?.join(
                  ","
                )}
              />
            </div>
            <div className="mt-5 flex justify-end gap-3">
              {isPendingValidation ? (
                <Button
                  variant={"outline"}
                  className="flex items-center gap-3"
                  disabled
                >
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Processing...
                  </>
                </Button>
              ) : (
                <>
                  <Button
                    variant={"outline"}
                    className="flex items-center gap-3"
                    onClick={() => handleValidasi(0)}
                    disabled={isPendingValidation}
                  >
                    <MdBlockFlipped />
                    Tidak Valid
                  </Button>
                  <Button
                    className="flex items-center gap-3"
                    onClick={() => handleValidasi(1)}
                    disabled={isPendingValidation}
                  >
                    <FaCheck />
                    Valid
                  </Button>
                </>
              )}
            </div>
          </Card>
        ) : (
          <div className="mt-5 flex justify-end">
            <Button
              variant="outline"
              className="flex items-center gap-3"
              onClick={() => handleReset()}
              disabled={isPendingResetValidation}
            >
              {isPendingResetValidation ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <GiBackwardTime />
                  Reset Hasil Validasi
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
